import { useEffect, useRef } from 'react';

function safeSerialize(answer: unknown): string | null {
    try {
        return JSON.stringify(answer);
    } catch {
        return null;
    }
}

export interface UsePeriodicAnswerFlushOptions {
    /** Current learner answer (undefined = skip flush). */
    answer: unknown;
    /** Interval in seconds; values are clamped to [3, 600]. */
    intervalSec: number;
    /** When false, no timer is registered. */
    enabled: boolean;
    /** Reset dedupe when content identity changes. */
    contentId: string;
    onFlush: (answer: unknown) => void;
}

/**
 * Calls `onFlush` at most every `intervalSec` when `answer` serializes to a new value.
 * Uses refs so the timer is not reset on every keystroke.
 */
export function usePeriodicAnswerFlush(options: UsePeriodicAnswerFlushOptions): void {
    const { answer, intervalSec, enabled, contentId, onFlush } = options;
    const answerRef = useRef(answer);
    const onFlushRef = useRef(onFlush);
    const lastSerializedRef = useRef('');

    answerRef.current = answer;
    onFlushRef.current = onFlush;

    useEffect(() => {
        lastSerializedRef.current = '';
    }, [contentId]);

    useEffect(() => {
        if (!enabled || intervalSec <= 0) {
            return undefined;
        }
        const sec = Math.min(600, Math.max(3, intervalSec));
        const ms = sec * 1000;
        const id = window.setInterval(() => {
            const current = answerRef.current;
            if (current === undefined) {
                return;
            }
            const serialized = safeSerialize(current);
            if (serialized === null || serialized === lastSerializedRef.current) {
                return;
            }
            lastSerializedRef.current = serialized;
            onFlushRef.current(current);
        }, ms);
        return () => window.clearInterval(id);
    }, [enabled, intervalSec]);
}
